/*
 * Admin Login Component
 * 
 * Separate authentication interface for admin panel access
 * Features:
 * - Admin-specific login form
 * - Default credentials display for initial setup
 * - Back to user login navigation
 * 
 * TO EDIT ADMIN LOGIN:
 * - Modify default credentials display in the info panel
 * - Update form validation in handleSubmit
 * - Change styling by updating Tailwind classes
 */

import React, { useState } from 'react';
import { useAdminAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { adminLogin } = useAdminAuth();

  /**
   * Handle form submission and admin authentication
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Attempt to login admin
    const result = await adminLogin(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  /**
   * Fill form with default admin credentials for easy testing
   */
  const fillDefaultCredentials = () => {
    setEmail('mark@email.com');
    setPassword('admin123');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800">Admin Panel</h2>
          <p className="text-gray-600 mt-2">Administrator access required</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Admin Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
              placeholder="Enter admin email"
            />
          </div>

          {/* Admin Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
              placeholder="Enter admin password"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Admin Sign In'
            )}
          </button>
        </form>

        {/* Default Admin Credentials Info */}
        {/* <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <div className="flex justify-between items-start mb-2">
            <h4 className="text-sm font-medium text-yellow-800">Default Admin Access:</h4>
            <button
              onClick={fillDefaultCredentials}
              className="text-xs text-yellow-600 hover:text-yellow-800 underline"
            >
              Fill Form
            </button>
          </div>
          <div className="text-sm text-yellow-700 space-y-1">
            <p><strong>Email:</strong> mark@email.com</p>
            <p><strong>Password:</strong> admin123</p>
            <p className="text-xs italic text-yellow-600 mt-2">
              ⚠️ Please change this password after first login!
            </p>
          </div>
        </div> */}

        {/* Admin Features Preview */}
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded">
          <h4 className="text-sm font-medium text-purple-800 mb-2">Admin Panel Features:</h4>
          <ul className="text-xs text-purple-700 space-y-1">
            <li>• Manage vehicle registration requests</li>
            <li>• Approve advertisement campaigns</li>
            <li>• Add public buses and routes</li>
            <li>• Create additional admin users</li>
            <li>• View system statistics</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;